import React from 'react';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import {
  DatePickerView,
  KeyboardDatePicker,
  KeyboardDatePickerProps,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import PickerToolbar from '@material-ui/pickers/_shared/PickerToolbar';
import { Controller, DeepMap, FieldError, RegisterOptions, useFormContext } from 'react-hook-form';
import frLocale from 'date-fns/locale/fr';
import enUSLocale from 'date-fns/locale/en-US';
import enCALocale from 'date-fns/locale/en-CA';
import DateFnsUtils from '@date-io/date-fns';
import { get } from 'utility/get';
import startOfDay from 'date-fns/startOfDay';
import { toolbarStyles, wpDatePickerStyles } from './wpDatePickerStyles';
import clsx from 'clsx';
import { useDatePickerPosition } from './useDatePickerPosition';
import { format as formatDate } from 'date-fns';
import Box from '@material-ui/core/Box';
import WpIcon from 'components/wp-icon';
import arrowDownOutline from 'components/wp-icon/icons/arrow-down-outline';
import WpButton from 'components/wp-button';
import WpToolTip from 'components/wp-tooltip';
import infoCircleSolid from 'components/wp-icon/icons/info-circle-solid';

export interface IWpDatePicker extends Partial<KeyboardDatePickerProps> {
  errorobj?: DeepMap<Record<string, any>, FieldError>;
  name: string;
  callBackOnChange?: (selectedValue: Date | null) => void;
  recommendedDates?: Array<Date | string>;
  locale?: LanguageTypeEnum;
  formatType?: WpDatePickerFormatTypeEnum;
  placeholderType?: WpDatePickerPlaceholderTypeEnum;
  rules?: Exclude<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
  keydownEvent?: any;
  disableWeekdays?: boolean;
  hideYearSelect?: boolean;
  openTo?: DatePickerView;
  useCustomToolbar?: boolean;
  tooltip?: string;
}

export enum LanguageTypeEnum {
  NotDefined = 0,
  EnglishUs = 1,
  EnglishCa = 2,
  FrenchCa = 3,
}

export enum WpDatePickerFormatTypeEnum {
  default = 'yyyy-MM-dd',
  ROE = 'dd/MM/yyyy',
}

export enum WpDatePickerPlaceholderTypeEnum {
  default = 'YYYY-MM-DD',
  ROE = 'DD/MM/YYYY',
}

const localeMap = {
  [LanguageTypeEnum.NotDefined]: enUSLocale,
  [LanguageTypeEnum.EnglishUs]: enUSLocale,
  [LanguageTypeEnum.EnglishCa]: enCALocale,
  [LanguageTypeEnum.FrenchCa]: frLocale,
};

const CustomToolbar = function (props: any) {
  const { date, isLandscape, openView, setOpenView, title } = props;

  const handleChangeViewClick = (view: string) => () => {
    setOpenView(view);
  };
  const classes = toolbarStyles();

  return (
    <PickerToolbar title={title} isLandscape={isLandscape} className={classes.toolbar}>
      <Box mx={1} display="inline">
        <WpButton
          onClick={handleChangeViewClick('month')}
          variant="text"
          color="secondary"
          style={{ opacity: openView === 'month' ? '1' : '0.6', margin: 0 }}
        >
          {formatDate(date, 'LLLL')}
          <Box mx={1} display="inline">
            <WpIcon color={openView === 'month' ? 'primary' : 'black'} size={8} svgIcon={arrowDownOutline} />
          </Box>
        </WpButton>
      </Box>
      <Box mx={1} display="inline">
        <WpButton
          onClick={handleChangeViewClick('year')}
          variant="text"
          color="secondary"
          style={{ opacity: openView === 'year' ? '1' : '0.6', margin: 0 }}
        >
          {formatDate(date, 'yyyy')}
          <Box mx={1} display="inline">
            <WpIcon color={openView === 'year' ? 'primary' : 'black'} size={8} svgIcon={arrowDownOutline} />
          </Box>
        </WpButton>
      </Box>
    </PickerToolbar>
  );
};

export const WpDatePicker = React.forwardRef(function WpDatePicker(props: IWpDatePicker, ref) {
  const { control } = useFormContext();
  const classes = wpDatePickerStyles();
  const {
    name,
    required,
    format,
    placeholder,
    callBackOnChange,
    errorobj = {},
    minDate,
    maxDate,
    fullWidth,
    disabled = false,
    recommendedDates,
    locale = LanguageTypeEnum.EnglishCa,
    formatType = WpDatePickerFormatTypeEnum.default,
    placeholderType = WpDatePickerPlaceholderTypeEnum.default,
    rules,
    InputProps,
    defaultValue,
    keydownEvent,
    shouldDisableDate = () => false,
    autoFocus = false,
    disableWeekdays = false,
    hideYearSelect = true,
    openTo = 'date',
    useCustomToolbar = false,
    renderDay,
  } = props;
  const error = get(errorobj, name);
  const errorMessage = error?.message;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const { anchorOrigin, transformOrigin } = useDatePickerPosition(containerRef);

  const handleRenderDay = (
    day: MaterialUiPickersDate,
    selectedDate: MaterialUiPickersDate,
    dayInCurrentMonth: boolean,
    dayComponent: JSX.Element
  ) => {
    const isSelected = selectedDate?.getTime() === day?.getTime();
    const isRecommended =
      day &&
      dayInCurrentMonth &&
      !isSelected &&
      !!recommendedDates?.find((date) => startOfDay(new Date(date)).getTime() === startOfDay(day).getTime());
    return (
      <div className={clsx(classes.day, { recommended: isRecommended, selected: isSelected })}>
        {isRecommended && (
          <svg width="27" height="25" viewBox="0 0 27 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.8793 0.869652L8.73376 7.3337L1.696 8.37361C0.433927 8.55913 -0.071867 10.1361 0.843378 11.0393L5.93503 16.068L4.73076 23.1716C4.51399 24.4556 5.84833 25.4174 6.96589 24.8169L13.2618 21.4628L19.5577 24.8169C20.6753 25.4125 22.0096 24.4556 21.7929 23.1716L20.5886 16.068L25.6803 11.0393C26.5955 10.1361 26.0897 8.55913 24.8276 8.37361L17.7899 7.3337L14.6443 0.869652C14.0807 -0.282549 12.4477 -0.297196 11.8793 0.869652Z" />
          </svg>
        )}
        {renderDay?.(day, selectedDate, dayInCurrentMonth, dayComponent) ?? dayComponent}
      </div>
    );
  };

  const isWeekend = (date: any) => {
    const day = date.getDay();
    return day === 0 || day === 6 || shouldDisableDate?.(date);
  };

  return (
    <Controller
      innerRef={ref}
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={{ ...rules, required }}
      render={({ onChange, onBlur, value, name, ref }) => {
        return (
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[locale]}>
            <div ref={containerRef} className={classes.container}>
              {props.label &&
                (props.tooltip ? (
                  <WpToolTip tooltype={2} title={props.tooltip} arrow>
                    <span>
                      <InputLabel htmlFor={props.id}>
                        {props.label}
                        <WpIcon svgIcon={infoCircleSolid} size={13} color="black" gutter="left" />
                      </InputLabel>
                    </span>
                  </WpToolTip>
                ) : (
                  <InputLabel htmlFor={props.id}>{props.label}</InputLabel>
                ))}

              <KeyboardDatePicker
                openTo={openTo}
                orientation={'portrait'}
                views={['year', 'month', 'date']}
                ToolbarComponent={useCustomToolbar ? CustomToolbar : undefined}
                fullWidth={fullWidth}
                minDate={minDate || undefined}
                maxDate={maxDate || undefined}
                autoOk
                error={Boolean(error)}
                placeholder={placeholder ? placeholder : placeholderType}
                format={format ? format : formatType}
                inputRef={ref}
                allowKeyboardControl
                PopoverProps={{
                  anchorOrigin,
                  transformOrigin,
                  marginThreshold: -30,
                  PaperProps: {
                    variant: 'outlined',
                    className: classes.paper,
                  },
                }}
                disableToolbar={hideYearSelect}
                variant="inline"
                inputVariant="outlined"
                name={name}
                onBlur={() => {
                  onBlur();
                }}
                onChange={(date) => {
                  onChange(date);
                  callBackOnChange?.(date);
                }}
                autoFocus={autoFocus}
                onKeyDown={keydownEvent}
                value={value || null}
                disabled={disabled}
                InputAdornmentProps={{ position: 'end' }}
                InputProps={{
                  // NOTE: We can't use the InputProps.endAdornment prop because it's overwritten by lib
                  // Problem here: https://github.com/mui/material-ui-pickers/blob/v3-x/lib/src/_shared/KeyboardDateInput.tsx#L107-L113
                  ...InputProps,
                  className: clsx(InputProps?.className, { ['has-selected']: !!value }),
                }}
                shouldDisableDate={disableWeekdays ? isWeekend : shouldDisableDate}
                keyboardIcon={
                  <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M11.6076 1.875H10.2148V0.351562C10.2148 0.158203 10.0581 0 9.86656 0H9.63442C9.4429 0 9.2862 0.158203 9.2862 0.351562V1.875H3.71477V0.351562C3.71477 0.158203 3.55808 0 3.36656 0H3.13442C2.9429 0 2.7862 0.158203 2.7862 0.351562V1.875H1.39335C0.624372 1.875 0.000488281 2.50488 0.000488281 3.28125V13.5938C0.000488281 14.3701 0.624372 15 1.39335 15H11.6076C12.3766 15 13.0005 14.3701 13.0005 13.5938V3.28125C13.0005 2.50488 12.3766 1.875 11.6076 1.875ZM1.39335 2.8125H11.6076C11.863 2.8125 12.0719 3.02344 12.0719 3.28125V4.6875H0.92906V3.28125C0.92906 3.02344 1.13799 2.8125 1.39335 2.8125ZM11.6076 14.0625H1.39335C1.13799 14.0625 0.92906 13.8516 0.92906 13.5938V5.625H12.0719V13.5938C12.0719 13.8516 11.863 14.0625 11.6076 14.0625ZM4.29513 9.375H3.13442C2.9429 9.375 2.7862 9.2168 2.7862 9.02344V7.85156C2.7862 7.6582 2.9429 7.5 3.13442 7.5H4.29513C4.48665 7.5 4.64335 7.6582 4.64335 7.85156V9.02344C4.64335 9.2168 4.48665 9.375 4.29513 9.375ZM7.08085 9.375H5.92013C5.72861 9.375 5.57192 9.2168 5.57192 9.02344V7.85156C5.57192 7.6582 5.72861 7.5 5.92013 7.5H7.08085C7.27236 7.5 7.42906 7.6582 7.42906 7.85156V9.02344C7.42906 9.2168 7.27236 9.375 7.08085 9.375ZM9.86656 9.375H8.70584C8.51433 9.375 8.35763 9.2168 8.35763 9.02344V7.85156C8.35763 7.6582 8.51433 7.5 8.70584 7.5H9.86656C10.0581 7.5 10.2148 7.6582 10.2148 7.85156V9.02344C10.2148 9.2168 10.0581 9.375 9.86656 9.375ZM7.08085 12.1875H5.92013C5.72861 12.1875 5.57192 12.0293 5.57192 11.8359V10.6641C5.57192 10.4707 5.72861 10.3125 5.92013 10.3125H7.08085C7.27236 10.3125 7.42906 10.4707 7.42906 10.6641V11.8359C7.42906 12.0293 7.27236 12.1875 7.08085 12.1875ZM4.29513 12.1875H3.13442C2.9429 12.1875 2.7862 12.0293 2.7862 11.8359V10.6641C2.7862 10.4707 2.9429 10.3125 3.13442 10.3125H4.29513C4.48665 10.3125 4.64335 10.4707 4.64335 10.6641V11.8359C4.64335 12.0293 4.48665 12.1875 4.29513 12.1875ZM9.86656 12.1875H8.70584C8.51433 12.1875 8.35763 12.0293 8.35763 11.8359V10.6641C8.35763 10.4707 8.51433 10.3125 8.70584 10.3125H9.86656C10.0581 10.3125 10.2148 10.4707 10.2148 10.6641V11.8359C10.2148 12.0293 10.0581 12.1875 9.86656 12.1875Z"
                      fill={error ? '#BF4240' : '#B2A7A7'}
                    />
                  </svg>
                }
                renderDay={handleRenderDay}
              />
              {!!error && <FormHelperText error>{errorMessage}</FormHelperText>}
            </div>
          </MuiPickersUtilsProvider>
        );
      }}
    />
  );
});
